import { Select, TextInput } from '@octano/global-ui';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, FormGroup, Row } from 'reactstrap';
import SectionTitle from '../../../../../../../components/text/SectionTitle';
import {
  useFilteredCities,
  useFilteredCommunes,
} from '../../../../../../../hooks/useFilteredCommunes';
import { useParameters } from '../../../../../../../hooks/useParameters';
import { useValidations } from '../../../../../../../hooks/useValidations';
import { calculateAge, formatDate } from '../../../../../../../utils/dates';
import {
  childrensOptions,
  drivingLicenseOptions,
  maritalStatusOptions,
  sexCIOptions,
} from '../../../../../../../utils/selectOptions';
import StudentPhoto, {
  StudentPhotoTexts,
} from '../../../../../../TuitionProcess/parts/StudentFile/StudentPhoto';

const CHILE_LOWER_CASE = 'chile';

type StudentBackgroundNoSuaPDIProps = {
  postulantId?: string | number;
  photo: string | null;
  isStudentFileUpdate?: boolean;
  optionalFields?: string[];
  fieldNames?: {
    identificationType: string;
    identification: string;
    passportCountry: string;
    namesStudent: string;
    paternalSurname: string;
    maternalSurname: string;
    graduatedYear: string;
    age: string;
    birthday: string;
    nationality: string;
    bordCountry: string;
    bordRegion: string;
    bordCommune: string;
    bordCity: string;
    bordCustomCity: string;
    bordLocation: string;
    sexID: string;
    maritalStatus: string;
    hasChildrens: string;
    hasDrivingLicense: string;
    personalEmail: string;
    officialEmail: string;
  };
  disabled?: boolean;
  hasEmail?: boolean;
  readOnly?: boolean;
};

/**
 * Sección de Antecedentes del estudiante SIN SUA
 */

const StudentBackgroundNoSuaPDI = ({
  postulantId,
  photo,
  isStudentFileUpdate = false,
  readOnly = false,
  optionalFields = [],
  fieldNames = {
    identificationType: 'identificationType',
    identification: 'identification',
    passportCountry: 'passportCountry',
    nationality: 'nationality',
    namesStudent: 'namesStudent',
    paternalSurname: 'paternalSurname',
    maternalSurname: 'maternalSurname',
    graduatedYear: 'graduatedYear',
    age: 'age',
    birthday: 'birthday',
    bordCountry: 'bordCountry',
    bordRegion: 'bordRegion',
    bordCommune: 'bordCommune',
    bordCity: 'bordCity',
    bordCustomCity: 'bordCustomCity',
    bordLocation: 'bordLocation',
    sexID: 'sexID',
    maritalStatus: 'maritalStatus',
    hasChildrens: 'hasChildrens',
    hasDrivingLicense: 'hasDrivingLicense',
    personalEmail: 'personalEmail',
    officialEmail: 'officialEmail',
  },
}: StudentBackgroundNoSuaPDIProps) => {
  const { control, watch, formState, setValue } = useFormContext();
  const { t } = useTranslation();
  const { validateTextNotEmpty } = useValidations();
  const { countryOptions, regionOptions, nationalityOptions } = useParameters();

  const prefix = `tuitionProcess.onSite.studentFile.backgroundSectionPDI`;

  const [rut, birthday, officialEmail, identificationTypeId] = watch([
    'rut',
    'birthday',
    'officialEmail',
    'identificationTypeId',
  ]);

  const studentPhotoTexts = t<any, StudentPhotoTexts>(
    `tuitionProcess.onSite.studentFile.studentPhoto`,
    { returnObjects: true },
  );

  const handleCalculate = useMemo(() => {
    if (birthday) {
      return calculateAge(birthday);
    }
    return null;
  }, [birthday]);

  // Handle Regions
  const [selectedCountry, selectedRegion] = watch([
    fieldNames.bordCountry,
    fieldNames.bordRegion,
  ]);
  const filteredCommunes = useFilteredCommunes(selectedRegion);
  const filteredCities = useFilteredCities(selectedRegion);

  const isRegionTouched = formState.touchedFields[fieldNames.bordRegion];
  const isChile = useMemo(() => {
    return String(selectedCountry?.label)
      .toLowerCase()
      .includes(CHILE_LOWER_CASE);
  }, [selectedCountry?.label]);

  useEffect(() => {
    if (rut) {
      setValue(
        fieldNames.bordCountry,
        countryOptions.find(
          (c) => c.label.toLocaleLowerCase() === CHILE_LOWER_CASE,
        ),
      );
    }
  }, [countryOptions, fieldNames.bordCountry, rut, setValue]);

  useEffect(() => {
    if (isRegionTouched) {
      setValue(fieldNames.bordCommune, null);
      setValue(fieldNames.bordCity, null);
    }
  }, [
    selectedRegion,
    setValue,
    isRegionTouched,
    fieldNames.bordCommune,
    fieldNames.bordCity,
  ]);

  const optional = (value: string, fieldName: string) => {
    if (optionalFields.includes(fieldName)) {
      return undefined;
    }
    return validateTextNotEmpty(value);
  };

  return (
    <>
      <Row>
        <Col xs={12}>
          <SectionTitle text={t(`${prefix}.title`)} />
        </Col>
      </Row>
      <Row>
        <Col className="pb-5" xs={12} lg="auto">
          <FormGroup>
            <div className="m-auto mw-100" style={{ width: '121px' }}>
              <StudentPhoto
                postulantId={postulantId}
                texts={studentPhotoTexts}
                defaultPhoto={photo}
                isStudentFileUpdate={isStudentFileUpdate}
                disabledUpdate={readOnly}
              />
            </div>
          </FormGroup>
        </Col>
        <Col xs={12} lg={true}>
          <Row>
            <Col className="pb-3" xs={12} lg={6}>
              <TextInput
                name={`${fieldNames.identificationType}`}
                label={t(`${prefix}.${fieldNames.identificationType}`)}
                rules={{ validate: validateTextNotEmpty }}
                control={control}
                disabled
                shouldUnregister={true}
              />
            </Col>
            {identificationTypeId === 'PASSPORT' && (
              <Col xs={5} className="pr-1 pr-md-2">
                <Select
                  name={`${fieldNames.passportCountry}`}
                  label={t(`${prefix}.${fieldNames.passportCountry}`)}
                  options={countryOptions}
                  isClearable={false}
                  control={control}
                  shouldUnregister={true}
                  disabled
                />
              </Col>
            )}
            <Col className="pb-3" xs={12} lg={6}>
              <TextInput
                name={`${fieldNames.identification}`}
                label={t(`${prefix}.${fieldNames.identification}`)}
                rules={{ validate: validateTextNotEmpty }}
                control={control}
                disabled
                shouldUnregister={true}
              />
            </Col>
            <Col className="pb-3" xs={12} lg={6}>
              <TextInput
                name={`${fieldNames.namesStudent}`}
                label={t(`${prefix}.${fieldNames.namesStudent}`)}
                control={control}
                disabled={!isStudentFileUpdate}
                readOnly={readOnly}
              />
            </Col>
            <Col className="pb-3" xs={12} lg={6}>
              <TextInput
                name={`${fieldNames.paternalSurname}`}
                label={t(`${prefix}.${fieldNames.paternalSurname}`)}
                control={control}
                disabled={!isStudentFileUpdate}
                readOnly={readOnly}
              />
            </Col>
            <Col className="pb-3" xs={12} lg={6}>
              <TextInput
                name={`${fieldNames.maternalSurname}`}
                label={t(`${prefix}.${fieldNames.maternalSurname}`)}
                control={control}
                disabled={!isStudentFileUpdate}
                readOnly={readOnly}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <Row>
        <Col className="pb-3" xs={12} lg={4}>
          <TextInput
            name={`${fieldNames.birthday}`}
            label={t(`${prefix}.${fieldNames.birthday}`)}
            value={birthday ? formatDate(birthday, 'DD-MM-YYYY') : ''}
            disabled
          />
        </Col>
        <Col className="pb-3" xs={12} lg={4}>
          <TextInput
            name={`${fieldNames.age}`}
            label={t(`${prefix}.${fieldNames.age}`)}
            value={handleCalculate}
            disabled={birthday}
          />
        </Col>
        <Col className="pb-3" xs={12} lg={4}>
          <Select
            name={`${fieldNames.nationality}`}
            label={t(`${prefix}.${fieldNames.nationality}`)}
            control={control}
            options={nationalityOptions}
            isSearchable={true}
            rules={{ validate: validateTextNotEmpty }}
            disabled={readOnly}
          />
        </Col>
        <Col className="pb-3" xs={12} lg={4}>
          <Select
            name={fieldNames.bordCountry}
            label={t(`${prefix}.${fieldNames.bordCountry}`)}
            options={countryOptions}
            control={control}
            rules={{
              validate: {
                require: (value) => {
                  return optional(value, fieldNames.bordCountry);
                },
              },
            }}
            disabled={isChile}
          />
        </Col>
        {isChile ? (
          <>
            <Col className="pb-3" xs={12} lg={4}>
              <Select
                name={fieldNames.bordRegion}
                label={t(`${prefix}.${fieldNames.bordRegion}`)}
                options={regionOptions}
                control={control}
                rules={{
                  validate: {
                    require: (value) => optional(value, fieldNames.bordRegion),
                  },
                }}
              />
            </Col>
            <Col className="pb-3" xs={12} lg={4}>
              <Select
                name={fieldNames.bordCity}
                label={t(`${prefix}.${fieldNames.bordCity}`)}
                options={filteredCities}
                control={control}
                rules={{
                  validate: {
                    require: (value) => {
                      return optional(value, fieldNames.bordCity);
                    },
                  },
                }}
                shouldUnregister
              />
            </Col>
            <Col className="pb-3" xs={12} lg={4}>
              <Select
                name={fieldNames.bordCommune}
                label={t(`${prefix}.${fieldNames.bordCommune}`)}
                options={filteredCommunes}
                control={control}
                rules={{
                  validate: {
                    require: (value) => optional(value, fieldNames.bordCommune),
                  },
                }}
                shouldUnregister
              />
            </Col>
          </>
        ) : (
          <>
            <Col className="pb-3" xs={12} lg={4}>
              <TextInput
                name={fieldNames.bordLocation}
                label={t(`${prefix}.${fieldNames.bordLocation}`)}
                control={control}
                rules={{
                  validate: {
                    require: (value) =>
                      optional(value, fieldNames.bordLocation),
                  },
                }}
                shouldUnregister
              />
            </Col>
            <Col className="pb-3" xs={12} lg={4}>
              <TextInput
                name={fieldNames.bordCustomCity}
                label={t(`${prefix}.${fieldNames.bordCity}`)}
                control={control}
                rules={{
                  validate: {
                    require: (value) =>
                      optional(value, fieldNames.bordCustomCity),
                  },
                }}
                shouldUnregister
              />
            </Col>
          </>
        )}
        <Col className="pb-3" xs={12} lg={4}>
          <Select
            name={`${fieldNames.sexID}`}
            label={t(`${prefix}.${fieldNames.sexID}`)}
            options={sexCIOptions}
            isSearchable={false}
            control={control}
            rules={{ validate: validateTextNotEmpty }}
            disabled={readOnly}
          />
        </Col>
        <Col className="pb-3" xs={12} lg={4}>
          <Select
            name={`${fieldNames.maritalStatus}`}
            label={t(`${prefix}.${fieldNames.maritalStatus}`)}
            options={maritalStatusOptions}
            isSearchable={false}
            control={control}
            rules={{ validate: validateTextNotEmpty }}
            disabled={readOnly}
          />
        </Col>
        <Col className="pb-3" xs={12} lg={4}>
          <Select
            name={`${fieldNames.hasChildrens}`}
            label={t(`${prefix}.${fieldNames.hasChildrens}`)}
            options={childrensOptions}
            isSearchable={false}
            control={control}
            rules={{ validate: validateTextNotEmpty }}
            disabled={readOnly}
          />
        </Col>
        <Col className="pb-3" xs={12} lg={4}>
          <Select
            name={`${fieldNames.hasDrivingLicense}`}
            label={t(`${prefix}.${fieldNames.hasDrivingLicense}`)}
            options={drivingLicenseOptions}
            isSearchable={false}
            control={control}
            rules={{ validate: validateTextNotEmpty }}
            disabled={readOnly}
          />
        </Col>

        {isStudentFileUpdate && (
          <>
            <Col className="pb-3" xs={12} lg={4}>
              <TextInput
                name={`${fieldNames.personalEmail}`}
                label={t(`${prefix}.${fieldNames.personalEmail}`)}
                control={isStudentFileUpdate ? control : undefined}
                rules={{ validate: validateTextNotEmpty }}
                readOnly={readOnly}
              />
            </Col>
            <Col className="pb-3" xs={12} lg={4}>
              <TextInput
                name={`${fieldNames.officialEmail}`}
                label={t(`${prefix}.${fieldNames.officialEmail}`)}
                control={isStudentFileUpdate ? control : undefined}
                disabled={isStudentFileUpdate ? !officialEmail : true}
                tooltip={t(`${prefix}.tooltip.email`)}
                readOnly={readOnly}
              />
            </Col>
          </>
        )}
        <Col className="pb-3" xs={12} lg={4}>
          <TextInput
            name={`${fieldNames.graduatedYear}`}
            label={t(`${prefix}.${fieldNames.graduatedYear}`)}
            control={control}
            shouldUnregister={true}
          />
        </Col>
      </Row>
    </>
  );
};

export default StudentBackgroundNoSuaPDI;
